import React, { useState, useEffect } from 'react';
import { Loader2, Users, Mail, MapPin, Calendar, Trash2, AlertTriangle, ShieldCheck, User, Edit, Plus, X, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

import { API_URL, BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editingAddress, setEditingAddress] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/clients`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setEditForm({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      addresses: client.addresses || []
    });
    setEditMode(true);
  };

  const handleSave = async () => {
    if (!selectedClient) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/clients/${selectedClient.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        console.log('Success:', 'Client mis à jour');
        fetchClients();
        setEditMode(false);
        // Update selected client with new data
        setSelectedClient({ ...selectedClient, ...editForm });
      } else {
        const err = await response.json();
        console.log('Error:', err.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/clients/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        console.log('Success:', 'Client supprimé');
        setClients(prev => prev.filter(c => c.user_id !== userId));
        if (selectedClient?.user_id === userId) {
          setSelectedClient(null);
        }
        setDeleteConfirm(null);
      } else {
        const err = await response.json();
        console.log('Error:', err.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    }
  };

  const addAddress = () => {
    setEditForm(prev => ({
      ...prev,
      addresses: [...prev.addresses, {
        address_id: `addr_${Date.now()}`,
        label: '',
        first_name: '',
        last_name: '',
        address_line1: '',
        address_line2: '',
        postal_code: '',
        city: '',
        country: 'France',
        phone: ''
      }]
    }));
  };

  const updateAddress = (index, field, value) => {
    setEditForm(prev => ({
      ...prev,
      addresses: prev.addresses.map((addr, i) => 
        i === index ? { ...addr, [field]: value } : addr
      )
    }));
  };

  const removeAddress = (index) => {
    setEditForm(prev => ({
      ...prev,
      addresses: prev.addresses.filter((_, i) => i !== index)
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">Clients</h1>
          <p className="text-sm text-fgs-text-muted mt-1">{clients.length} client(s) enregistré(s)</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Clients List */}
        <div className="lg:col-span-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-fgs-primary" />
            </div>
          ) : clients.length === 0 ? (
            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-12 text-center">
              <Users className="w-16 h-16 text-fgs-text-muted mx-auto mb-4" />
              <p className="text-fgs-text-muted">Aucun client inscrit</p>
            </div>
          ) : (
            <div className="space-y-3">
              {clients.map(client => (
                <div
                  key={client.user_id}
                  className={`bg-fgs-surface border rounded-xl p-4 transition-colors ${
                    selectedClient?.user_id === client.user_id 
                      ? 'border-fgs-primary' 
                      : 'border-fgs-border hover:border-fgs-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div 
                      className="flex items-center gap-3 flex-1 cursor-pointer"
                      onClick={() => setSelectedClient(client)}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        client.role === 'admin' ? 'bg-fgs-primary/20' : 'bg-fgs-bg'
                      }`}>
                        {client.role === 'admin' ? (
                          <ShieldCheck className="w-5 h-5 text-fgs-primary" />
                        ) : (
                          <User className="w-5 h-5 text-fgs-text-muted" />
                        )}
                      </div>
                      <div>
                        <span className="text-white font-medium">{client.name || 'Sans nom'}</span>
                        {client.role === 'admin' && (
                          <span className="ml-2 px-2 py-0.5 bg-fgs-primary/20 text-fgs-primary rounded text-xs">
                            Admin
                          </span>
                        )}
                        <p className="text-sm text-fgs-text-muted">{client.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(client);
                        }}
                        className="p-1.5 hover:bg-fgs-primary/20 rounded text-fgs-text-muted hover:text-fgs-primary transition-colors"
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {client.role !== 'admin' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirm(client.user_id);
                          }}
                          className="p-1.5 hover:bg-red-500/20 rounded text-fgs-text-muted hover:text-red-400 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div 
                    className="flex items-center gap-4 text-xs text-fgs-text-muted cursor-pointer"
                    onClick={() => setSelectedClient(client)}
                  >
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Inscrit le {formatDate(client.created_at)}
                    </span>
                    {client.addresses?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {client.addresses.length} adresse(s)
                      </span>
                    )}
                  </div>
                  
                  {/* Delete confirmation */}
                  {deleteConfirm === client.user_id && (
                    <div className="mt-3 pt-3 border-t border-fgs-border">
                      <div className="flex items-center gap-2 text-yellow-400 text-sm mb-2">
                        <AlertTriangle className="w-4 h-4" />
                        <span>Supprimer ce client ?</span>
                      </div>
                      <p className="text-xs text-fgs-text-muted mb-2">
                        Ses adresses et historique seront perdus.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(client.user_id)}
                          className="bg-red-500 hover:bg-red-600"
                        >
                          Supprimer
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeleteConfirm(null)}
                          className="border-fgs-border text-fgs-text-muted"
                        >
                          Annuler
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Client Details / Edit Panel */}
        <div className="lg:col-span-1">
          {editMode && selectedClient ? (
            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6 sticky top-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold text-white">Modifier le client</h3>
                <button
                  onClick={() => setEditMode(false)}
                  className="p-1 hover:bg-fgs-bg rounded"
                >
                  <X className="w-5 h-5 text-fgs-text-muted" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-fgs-text-muted text-xs">Nom</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                  />
                </div>

                <div>
                  <Label className="text-fgs-text-muted text-xs">Email</Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                  />
                </div>

                <div>
                  <Label className="text-fgs-text-muted text-xs">Téléphone</Label>
                  <Input
                    value={editForm.phone || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="06 12 34 56 78"
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                  />
                </div>

                {/* Addresses */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-fgs-text-muted text-xs">Adresses de livraison</Label>
                    <button
                      onClick={addAddress}
                      className="text-xs text-fgs-primary hover:text-fgs-primary-hover flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Ajouter
                    </button>
                  </div>
                  
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {editForm.addresses?.map((addr, idx) => (
                      <div key={addr.address_id || idx} className="bg-fgs-bg rounded-lg p-3 relative">
                        <button
                          onClick={() => removeAddress(idx)}
                          className="absolute top-2 right-2 p-1 hover:bg-red-500/20 rounded text-fgs-text-muted hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        
                        <div className="grid gap-2">
                          <Input
                            placeholder="Titre (ex: Domicile, Bureau)"
                            value={addr.label || ''}
                            onChange={(e) => updateAddress(idx, 'label', e.target.value)}
                            className="bg-fgs-surface border-fgs-border text-white text-sm"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              placeholder="Prénom"
                              value={addr.first_name || ''}
                              onChange={(e) => updateAddress(idx, 'first_name', e.target.value)}
                              className="bg-fgs-surface border-fgs-border text-white text-sm"
                            />
                            <Input
                              placeholder="Nom"
                              value={addr.last_name || ''}
                              onChange={(e) => updateAddress(idx, 'last_name', e.target.value)}
                              className="bg-fgs-surface border-fgs-border text-white text-sm"
                            />
                          </div>
                          <Input
                            placeholder="Adresse (ligne 1)"
                            value={addr.address_line1 || ''}
                            onChange={(e) => updateAddress(idx, 'address_line1', e.target.value)}
                            className="bg-fgs-surface border-fgs-border text-white text-sm"
                          />
                          <Input
                            placeholder="Adresse ligne 2 (bât., étage...)"
                            value={addr.address_line2 || ''}
                            onChange={(e) => updateAddress(idx, 'address_line2', e.target.value)}
                            className="bg-fgs-surface border-fgs-border text-white text-sm"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              placeholder="Code postal"
                              value={addr.postal_code || ''}
                              onChange={(e) => updateAddress(idx, 'postal_code', e.target.value)}
                              className="bg-fgs-surface border-fgs-border text-white text-sm"
                            />
                            <Input
                              placeholder="Ville"
                              value={addr.city || ''}
                              onChange={(e) => updateAddress(idx, 'city', e.target.value)}
                              className="bg-fgs-surface border-fgs-border text-white text-sm"
                            />
                          </div>
                          <Input
                            placeholder="Téléphone"
                            value={addr.phone || ''}
                            onChange={(e) => updateAddress(idx, 'phone', e.target.value)}
                            className="bg-fgs-surface border-fgs-border text-white text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full bg-fgs-primary hover:bg-fgs-primary-hover"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
              </div>
            </div>
          ) : selectedClient ? (
            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6 sticky top-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold text-white">Détails du client</h3>
                <button
                  onClick={() => handleEdit(selectedClient)}
                  className="p-1.5 hover:bg-fgs-primary/20 rounded text-fgs-text-muted hover:text-fgs-primary"
                  title="Modifier"
                >
                  <Edit className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <p className="text-fgs-text-muted text-xs">Nom</p>
                  <p className="text-white">{selectedClient.name || 'Non renseigné'}</p>
                </div>

                <div>
                  <p className="text-fgs-text-muted text-xs">Email</p>
                  <a 
                    href={`mailto:${selectedClient.email}`}
                    className="text-fgs-primary hover:underline flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    {selectedClient.email}
                  </a>
                </div>

                {selectedClient.phone && (
                  <div>
                    <p className="text-fgs-text-muted text-xs">Téléphone</p>
                    <p className="text-white">{selectedClient.phone}</p>
                  </div>
                )}

                <div>
                  <p className="text-fgs-text-muted text-xs">Rôle</p>
                  <p className="text-white capitalize">{selectedClient.role || 'Utilisateur'}</p>
                </div>

                <div>
                  <p className="text-fgs-text-muted text-xs">Inscrit le</p>
                  <p className="text-white">{formatDate(selectedClient.created_at)}</p>
                </div>

                {selectedClient.addresses?.length > 0 && (
                  <div>
                    <p className="text-fgs-text-muted text-xs mb-2">Adresses ({selectedClient.addresses.length})</p>
                    <div className="space-y-2">
                      {selectedClient.addresses.map((addr, idx) => (
                        <div key={idx} className="bg-fgs-bg rounded-lg p-3 text-sm">
                          <p className="text-white font-medium">{addr.label || 'Adresse'}</p>
                          {(addr.first_name || addr.last_name) && (
                            <p className="text-fgs-text-muted">{addr.first_name} {addr.last_name}</p>
                          )}
                          <p className="text-fgs-text-muted">{addr.address_line1}</p>
                          {addr.address_line2 && (
                            <p className="text-fgs-text-muted">{addr.address_line2}</p>
                          )}
                          <p className="text-fgs-text-muted">{addr.postal_code} {addr.city}</p>
                          {addr.phone && (
                            <p className="text-fgs-text-muted text-xs mt-1">{addr.phone}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6 text-center">
              <Users className="w-12 h-12 text-fgs-text-muted mx-auto mb-3" />
              <p className="text-fgs-text-muted text-sm">
                Sélectionnez un client pour voir ses détails
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminClients;
